import ButtonIcon from '../../../../components/buttons/ButtonIcon';
import { Box, MenuItem } from '@mui/material';
import { CustomTextField } from '../../../../components/CustomTextField';
import { CustomSelectAndChildren } from '../../../../components/CustomSelectAndChildren';
import DivContainerBody from '../../../../components/DivContainerBody';
import React from 'react';
import { useSelector } from 'react-redux';
import { CleanIcon } from '../../../../components/buttons/IconButtons';
import {
    ALLOWED_MODES_BY_DEVICE,
    DISABLED_ZONE_BY_MODE,
    getDeviceAccessTypeById,
    MODE_MODEL,
    STEPS_CREATE_DEVICE, DISABLE_CAMERA_ANTENNA
} from '../Model/Model';
import {CustomPassword} from "../../../../components/CustomPassword";
import {STATUS_MODEL} from "../../../../enums/statusEnum";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 * @param {Object} data - Current device data object
 * @param {boolean} reset - Flag indicating if form should be reset
 * @param {Function} handleChange - Handler for input changes
 * @param {Array} emptyFields - List of empty required fields for validation
 * @param {Function} t - Translation function
 * @param {Function} handleClear - Handler for clearing all inputs
 * @param {string} [id] - Optional device ID (presence indicates edit mode)
 * @param {Object} location - React router location object
 * @returns {JSX.Element} Device data entry form section
 * @returns {JSX.Element}
 * @constructor
 */

/**
 * Component used to configure
 * general device information.
 *
 * This component handles:
 * - Device type selection.
 * - Device access type selection.
 * - Device mode configuration.
 * - Zone assignment.
 * - Device network configuration.
 * - Password configuration.
 * - Device status selection.
 * - Device description configuration.
 * - Dynamic field restrictions by device type.
 *
 * @component SectionCreateDeviceData
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.data - Device form data.
 * @param {Function} props.handleChange - Updates form field values.
 * @param {boolean} props.emptyFields - Indicates validation errors.
 * @param {Function} props.t - Translation function.
 * @param {Function} props.handleClear - Clears general data step values.
 * @param {number|string|undefined} props.id - Device identifier.
 * @param {Object} props.location - React router location object.
 *
 * @returns {JSX.Element} General device configuration section.
 */
const SectionCreateDeviceData = ({
    data,
    handleChange,
    emptyFields,
    t,
    handleClear,
    id,
    location,
}) => {
    // Redux state selectors
    const { getAccessType } = useSelector((state) => state.EmployeeZonesSlice);
    const { getListStatusDevice } = useSelector(state => state.DevicesSlice);
    const { getDeviceType } = useSelector((state) => state.EmployeeZonesSlice);
    const { employeeWorkStations } = useSelector((state) => state.CompanyEmployeesSlice);
    const { zoneIdProps } = location.state || {};
    // Disable zone if it comes from props or if current mode is in disabled list
    const isZoneDisabled = zoneIdProps || DISABLED_ZONE_BY_MODE.includes(data?.modeId);

    // Resolve allowed modes: use device-specific config or fallback to all modes
    const allowedModes = ALLOWED_MODES_BY_DEVICE[data?.deviceType] || Object.values(MODE_MODEL);


    return (
        <DivContainerBody>
            <div className='row'>
                <div className='d-flex justify-content-end col-12 mb-2'>
                    {id !== undefined ? (
                        ''
                    ) : (
                        <ButtonIcon
                            className='btn-clean-secondary-600'
                            id='btn_cud_clean_all_inputs'
                            Icon={CleanIcon}
                            title={'clear_instances'}
                            onClick={() => {
                                handleClear(STEPS_CREATE_DEVICE.DATA.INDEX);
                            }}
                            iconProps='btn-icon-svg-12px icon-color-general-00'
                        />
                    )}
                </div>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_device_type'
                        name='deviceType'
                        value={data?.deviceType}
                        onChange={(evt) => {
                            handleChange(evt, 'deviceType', 'data');
                        }}
                        trans='device_type'
                        label='lbl_cud_device_type'
                        emptyFields={emptyFields}
                        required={true}
                        showNoSelectionOption={false}
                        disabled={data?.deviceId && true}
                    >
                        {getDeviceType?.map((item) => {
                            return <MenuItem value={item.id}>{item.name.replace(/_/g, ' ')}</MenuItem>;
                        })}
                    </CustomSelectAndChildren>
                </Box>
                {id !== undefined && (
                    <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                        <CustomTextField
                            id='txt_cud_id'
                            name='idDevice'
                            value={data?.idDevice}
                            trans='device_id'
                            onChange={(evt) => {
                                handleChange(evt, 'idDevice', 'data');
                            }}
                        />
                    </Box>
                )}
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_device_name'
                        name='deviceName'
                        emptyFields={emptyFields}
                        value={data?.deviceName}
                        trans='device_name'
                        required={true}
                        onChange={(evt) => {
                            handleChange(evt, 'deviceName', 'data');
                        }}
                        length={128}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_zone'
                        name='zoneName'
                        value={id !== undefined ? data?.zoneName : zoneIdProps ? zoneIdProps : data?.zoneName}
                        onChange={(evt) => {
                            const selectedId = evt.target.value;
                            const selectedItem = employeeWorkStations?.find((item) => item.id === selectedId);
                            handleChange({target: {name: 'zoneName', value: selectedId}}, 'zoneName', 'data');
                            handleChange({target: {name: 'zone',value: selectedItem?.name || ''}}, 'zone', 'data');
                        }}
                        trans='zone'
                        label='lbl_cud_zone'
                        showNoSelectionOption={true}
                        disabled={isZoneDisabled}
                    >
                        {employeeWorkStations?.map((item) => (
                            <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)
                        )}
                    </CustomSelectAndChildren>
                </Box>
                {id === undefined && (
                    <>
                        <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                            <CustomTextField
                                id='txt_cud_device_id'
                                name='idDevice'
                                value={data?.idDevice}
                                trans='device_id'
                                onChange={(evt) => {
                                    handleChange(evt, 'idDevice', 'data');
                                }}
                                length={128}
                            />
                        </Box>
                    </>
                )}
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_device_access_type'
                        name='accessType'
                        value={data?.accessType}
                        keyName='accessType'
                        onChange={(evt) => {
                            handleChange(evt, 'accessType','data');
                        }}
                        trans='device_access_type'
                        label='lbl_cud_devicestatus'
                        emptyFields={emptyFields}
                        showNoSelectionOption={false}
                        disabled={DISABLE_CAMERA_ANTENNA.includes(data.deviceType)}
                    >
                        {getAccessType?.map((item, index) => (
                            <MenuItem key={index} value={item.id}>
                                {t(getDeviceAccessTypeById(item?.id)?.trans)}
                            </MenuItem>
                        ))}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_mode_id'
                        name='modeId'
                        emptyFields={emptyFields}
                        value={data?.modeId}
                        trans='mode'
                        required={true}
                        onChange={(evt) => {
                            handleChange(evt, 'modeId','data');
                        }}
                        showNoSelectionOption={false}
                    >
                        {Object.entries(MODE_MODEL)
                            .filter(([_, value]) => allowedModes.includes(value))
                            .map(([item, value]) => (
                                <MenuItem key={value} value={value}>
                                    {item.replaceAll('_', ' ').toUpperCase()}
                                </MenuItem>
                            ))}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_ip_domain'
                        name='ip'
                        emptyFields={emptyFields}
                        value={data?.ip}
                        trans='ip_domain'
                        required={true}
                        onChange={(evt) => {
                            handleChange(evt, 'ip', 'data');
                        }}
                        length={64}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_mac'
                        name='mac'
                        emptyFields={emptyFields}
                        value={data?.mac}
                        trans='mac'
                        required={true}
                        onChange={(evt) => {
                            handleChange(evt, 'mac', 'data');
                        }}
                        length={64}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomTextField
                        id='txt_cud_s/n'
                        name='sn'
                        emptyFields={emptyFields}
                        value={data?.sn}
                        trans='s_n'
                        required={true}
                        onChange={(evt) => {
                            handleChange(evt, 'sn', 'data');
                        }}
                        length={128}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomPassword
                        id='txt_cud_password'
                        name='password'
                        value={data?.password}
                        trans='password'
                        emptyFields={!DISABLE_CAMERA_ANTENNA.includes(data.deviceType)  && emptyFields}
                        required={!DISABLE_CAMERA_ANTENNA.includes(data.deviceType)}
                        onChange={(evt) => {
                            handleChange(evt, 'password', 'data');
                        }}
                        length={16}
                        minLength={8}
                        label={'password'}
                        autoComplete={'off'}
                    />
                </Box>
                <Box className='col-sm-12 col-md-6 col-lg-6 col-xl-6'>
                    <CustomSelectAndChildren
                        id='slt_cud_status'
                        name='statusValue'
                        value={data?.statusValue}
                        onChange={(evt) => {
                            handleChange(evt, 'statusValue','data');
                        }}
                        list={getListStatusDevice}
                        trans='status'
                        label='lbl_cud_status'
                        emptyFields={emptyFields}
                        required={true}
                        showNoSelectionOption={false}
                    >
                        {getListStatusDevice?.map((item, index) => (
                            <MenuItem key={index} value={item.id}>
                                {item.id === STATUS_MODEL.ACTIVE_COMPANY
                                    ? t('active').toUpperCase()
                                    : t('inactive').toUpperCase()}
                            </MenuItem>
                        ))}
                    </CustomSelectAndChildren>
                </Box>
                <Box className='col-sm-12 col-md-12 col-lg-12 col-xl-12'>
                    <CustomTextField
                        id='txt_cud_description'
                        name='description'
                        value={data?.description}
                        trans='description'
                        onChange={(evt) => {
                            handleChange(evt, 'description', 'data');
                        }}
                        length={512}
                        multiline
                        rows={3}
                    />
                </Box>
            </div>
        </DivContainerBody>
    );
};

export default SectionCreateDeviceData;
